/**
 * Success state after Margaritaville `/form` account request submission (pending HR review).
 */

const APP_STORE_BADGE = "/badges/app-store-badge.svg";
const GOOGLE_PLAY_BADGE = "/badges/google-play-badge.png";

type AccountRequestSuccessViewProps = {
  email: string;
  /** e.g. "Camp Margaritaville — Resort & Cabana Cabins — Auburndale" */
  locationLabel: string;
  workvivoAppStoreUrl: string;
  workvivoPlayStoreUrl: string;
};

export function AccountRequestSuccessView({
  email,
  locationLabel,
  workvivoAppStoreUrl,
  workvivoPlayStoreUrl,
}: AccountRequestSuccessViewProps) {
  const trimmedEmail = email.trim();

  return (
    <div style={{ textAlign: "center" }}>
      <div
        style={{
          width: "80px",
          height: "80px",
          margin: "0 auto",
          borderRadius: "50%",
          background: "linear-gradient(160deg, var(--ct-teal) 0%, var(--ct-teal-dark) 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 10px 25px rgba(22, 160, 133, 0.35)",
        }}
        aria-hidden
      >
        <svg
          width="40"
          height="40"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--ct-white)"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>

      <h2
        style={{
          margin: 0,
          marginTop: "1.25rem",
          fontSize: "1.75rem",
          color: "var(--ct-slate)",
        }}
      >
        Thank you!
      </h2>

      <p style={{ marginTop: "0.75rem", color: "var(--ct-text)", lineHeight: 1.6 }}>
        Your account request was submitted successfully.
      </p>

      <div
        style={{
          marginTop: "1.5rem",
          padding: "1.25rem",
          borderRadius: "10px",
          backgroundColor: "#f8fafc",
          border: "1px solid var(--ct-border)",
          boxShadow: "var(--ct-card-shadow)",
          textAlign: "left",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "1rem", color: "var(--ct-text)", textAlign: "center" }}>
          What happens next?
        </h3>
        <ul
          style={{
            margin: 0,
            marginTop: "1rem",
            paddingLeft: "1.25rem",
            color: "var(--ct-text-muted)",
            lineHeight: 1.8,
          }}
        >
          <li>
            The HR department for{" "}
            <strong style={{ color: "var(--ct-text)" }}>{locationLabel}</strong> is reviewing your
            request.
          </li>
          <li>
            You&apos;ll receive an email at{" "}
            <strong style={{ color: "var(--ct-text)" }}>{trimmedEmail}</strong> when your account is
            ready.
          </li>
          <li>Use the official App Store and Google Play badges below to install Workvivo.</li>
          <li>
            When your request is approved, open the email from us and use the link inside to sign in
            and finish setting up your account.
          </li>
        </ul>
      </div>

      <p
        style={{
          marginTop: "1.25rem",
          fontSize: "0.875rem",
          color: "var(--ct-text-muted)",
          lineHeight: 1.55,
        }}
      >
        You&apos;ve submitted a request for{" "}
        <strong style={{ color: "var(--ct-text)" }}>{locationLabel}</strong>.
      </p>

      <div
        style={{
          marginTop: "1.75rem",
          padding: "1.25rem 1rem",
          borderRadius: "10px",
          backgroundColor: "var(--ct-teal-muted)",
          border: "1px solid rgba(22, 160, 133, 0.35)",
        }}
      >
        <p
          style={{
            margin: 0,
            marginBottom: "1rem",
            fontSize: "0.95rem",
            color: "var(--ct-teal-dark)",
            fontWeight: 600,
          }}
        >
          Get the Workvivo app while you wait
        </p>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem 1.25rem",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <a
            href={workvivoAppStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Download Workvivo on the App Store"
            style={{ display: "inline-block", lineHeight: 0 }}
          >
            <img
              src={APP_STORE_BADGE}
              alt=""
              height={40}
              width={120}
              style={{ height: "40px", width: "auto", display: "block" }}
            />
          </a>
          <a
            href={workvivoPlayStoreUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Get Workvivo on Google Play"
            style={{ display: "inline-block", lineHeight: 0 }}
          >
            <img
              src={GOOGLE_PLAY_BADGE}
              alt=""
              height={56}
              width={180}
              style={{ height: "56px", width: "auto", maxWidth: "min(100%, 200px)", display: "block" }}
            />
          </a>
        </div>
      </div>
    </div>
  );
}
